import { logError } from '@zg-rentals/log-error';
import {
  RentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising_Path,
  RentalManagerUserPropertySpotlightWebApiHandler_overview_Path,
} from '@zg-rentals/ts-rental-manager-api-client';

import {
  claimRowWrite,
  fetchAdvertisingPage,
  isAdvertisingPageKey,
  takeRowWriteSequence,
  writeRowIfNewest,
} from '../../../../hooks/queries/properties/zramAdvertisingPage';

import type { QueryClient } from '@zg-rentals/react-query5';
import type { AdvertisingPageKey } from '../../../../hooks/queries/properties/zramAdvertisingPage';
import type {
  AccountViewListingAdvertisingDto,
  AccountViewListingAdvertisingSpotlightDto,
  GetUserPropertySpotlightOverviewResponseDto,
  PlacementSource,
} from '@zg-rentals/ts-rental-manager-api-client';

const REMOVE_SPOTLIGHT_BUTTON_URL = 'SPOTLIGHT_REMOVE';
const APPLY_SPOTLIGHT_BUTTON_URL = 'SPOTLIGHT_APPLY';
const REMOVE_SPOTLIGHT_BUTTON_TEXT = 'Remove Spotlight';
const APPLY_SPOTLIGHT_BUTTON_TEXT = 'Apply Spotlight';
const EDITABLE_MANAGED_BY_FEED_BUTTON_URLS = new Set([
  APPLY_SPOTLIGHT_BUTTON_URL,
  'SPOTLIGHT_REASSIGN',
  REMOVE_SPOTLIGHT_BUTTON_URL,
]);

export const isManagedByFeedReadOnly = ({
  buttonUrl,
  managedByFeed,
}: {
  buttonUrl?: string;
  managedByFeed?: boolean;
}) => Boolean(managedByFeed && !EDITABLE_MANAGED_BY_FEED_BUTTON_URLS.has(buttonUrl ?? ''));

export const isRemovableManagedByFeedSpotlight = ({
  buttonUrl,
  managedByFeed,
}: {
  buttonUrl?: string;
  managedByFeed?: boolean;
}) => Boolean(managedByFeed && buttonUrl === REMOVE_SPOTLIGHT_BUTTON_URL);

/**
 * Pure function to build query predicate for advertising data
 */
export const buildAdvertisingQueryPredicate = (listingAlias: string) => ({
  predicate: (query: { queryKey: unknown }) => {
    const key = query.queryKey;
    return (
      Array.isArray(key) &&
      typeof key[0] === 'string' &&
      (key[0].includes('advertising') || key[0].includes('Advertising')) &&
      typeof key[1] === 'object' &&
      key[1] !== null &&
      (key[1] as { listingAlias?: string }).listingAlias === listingAlias
    );
  },
});

/**
 * Matches each row's own query and any page read that holds one of the rows, since a row read by its page never
 * fetches on its own. Invalidate with it, never write: a page entry holds a map of rows.
 */
export const buildAdvertisingRefreshPredicate = (listingAliases: string[]) => ({
  predicate: (query: { queryKey: unknown }) =>
    listingAliases.some((listingAlias) => buildAdvertisingQueryPredicate(listingAlias).predicate(query)) ||
    (isAdvertisingPageKey(query.queryKey) &&
      query.queryKey[1].listingAliases.some((listingAlias) => listingAliases.includes(listingAlias))),
});

/**
 * Re-reads single family advertising after a spotlight change on one row. Every row is refreshed, because the change
 * moves the available count every row shows, but each page is read once. Settles when the changed row's read does.
 */
export const refreshSingleFamilyAdvertisingAfterChange = (queryClient: QueryClient, listingAlias: string) => {
  const { predicate: holdsChangedRow } = buildAdvertisingRefreshPredicate([listingAlias]);
  void queryClient.invalidateQueries({
    predicate: (query) =>
      (isAdvertisingPageKey(query.queryKey) ||
        (Array.isArray(query.queryKey) &&
          query.queryKey[0] === RentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising_Path)) &&
      !holdsChangedRow(query),
  });
  return queryClient.refetchQueries({ predicate: holdsChangedRow });
};

/**
 * Pure function to build a predicate matching every listing's advertising query by key (not by meta.tags), so a
 * refresh reaches rows the engine changed as a side effect even if the tag-based invalidation misses them.
 */
export const buildAllAdvertisingQueryPredicate = () => ({
  predicate: (query: { queryKey: unknown }) => {
    const key = query.queryKey;
    return (
      Array.isArray(key) &&
      typeof key[0] === 'string' &&
      (key[0].includes('advertising') || key[0].includes('Advertising'))
    );
  },
});

const PROPERTIES_LIST_TAG = 'getProperties';
const SPOTLIGHT_OVERVIEW_TAG = 'spotlightOverview';

const buildOverviewQueryPredicate = () => ({
  predicate: ({ meta }: { meta?: Record<string, unknown> }) =>
    Array.isArray(meta?.tags) && meta.tags.includes(SPOTLIGHT_OVERVIEW_TAG),
});

/**
 * Invalidates the properties table and re-pulls every listing's advertising status, so rows the engine promoted or
 * demoted as a side effect of a spotlight change update in place. refetchType 'all' reaches rows that are briefly
 * unobserved during an interaction-driven re-render, which 'active' would skip.
 *
 * Settles once the overview and advertising reads land, without waiting for the properties list. The list carries no
 * spotlight state and takes 10-15s on large accounts, so waiting on it only held the table locked that much longer.
 */
export const refreshSpotlightTable = (queryClient: QueryClient) => {
  queryClient
    .invalidateQueries({ predicate: ({ meta }) => Boolean(meta?.tags?.includes(PROPERTIES_LIST_TAG)) })
    .catch((error) => logError({ error }));
  return Promise.all([
    queryClient.invalidateQueries(buildOverviewQueryPredicate()),
    queryClient.invalidateQueries({ ...buildAllAdvertisingQueryPredicate(), refetchType: 'all' }),
  ]).catch((error) => logError({ error }));
};

/**
 * Re-reads the totals past the API's per-instance placement cache and writes them into the cached overview query.
 * Invalidating cannot do this on its own: the refetch it triggers carries the original params, so it can be answered
 * from the very snapshot it was meant to replace, and the header keeps the pre-engine counts until a reload.
 *
 * Written as a direct read because the generated client does not carry the parameter yet. Once it does, this should
 * collapse into the generated overview call.
 */
export const refreshSpotlightOverviewPastCache = async (queryClient: QueryClient) => {
  try {
    const response = await fetch(
      `/rental-manager-api${RentalManagerUserPropertySpotlightWebApiHandler_overview_Path}?bypassCache=true`,
      { credentials: 'include', headers: { accept: 'application/json' } },
    );
    if (!response.ok) {
      throw new Error(`Spotlight overview bypass read failed with ${response.status}`);
    }
    const overview = (await response.json())?.response as GetUserPropertySpotlightOverviewResponseDto | undefined;
    if (!overview) {
      throw new Error('Spotlight overview bypass read returned no body');
    }
    queryClient.setQueriesData(buildOverviewQueryPredicate(), overview);
  } catch (error) {
    // The counts stay as the ordinary refresh left them rather than the table being blocked on this.
    logError({ error });
  }
};

/**
 * Re-reads every row that currently holds an advertising value, past the API's per-instance placement cache, and
 * writes each result back. The chip is drawn from placementSource, so a row whose last read landed on a pre-engine
 * snapshot shows no Spotlight chip and never corrects itself.
 *
 * Single family rows read by a page are re-read by that page, one bypass per page. Every other row still gets one
 * request of its own: a bypass skips the cache it just wrote, so the first row cannot warm the rest. That is why only
 * the refresh after a completed run does this, never the refetch fired straight from a mutation.
 *
 * The endpoint differs by customer type, so both the path and the alias are taken from the cached query key rather
 * than rebuilt, which keeps each row pointed at the endpoint it actually used.
 */
export const refreshSpotlightAdvertisingPastCache = async (queryClient: QueryClient) => {
  const cachedQueries = queryClient.getQueriesData(buildAllAdvertisingQueryPredicate());
  // Only pages on screen. A page left over from an earlier list is not re-read as a page; its rows that still hold a
  // value get their own read below, as every row did before pages.
  const activePageKeys = queryClient
    .getQueryCache()
    .findAll({ type: 'active', predicate: (query) => isAdvertisingPageKey(query.queryKey) })
    .map((query) => query.queryKey)
    .filter(isAdvertisingPageKey);
  const refreshedByPage = await refreshAdvertisingPagesPastCache(queryClient, activePageKeys);

  await Promise.all(
    cachedQueries.map(async ([queryKey, data]) => {
      // The other customer type's query sits in the cache disabled and empty; it has nothing to correct.
      if (data == null) {
        return;
      }
      const [path, params] = queryKey as [unknown, { listingAlias?: string } | undefined];
      const listingAlias = params?.listingAlias;
      if (typeof path !== 'string' || !listingAlias || refreshedByPage.has(listingAlias)) {
        return;
      }
      const sequence = takeRowWriteSequence();
      try {
        const response = await fetch(
          `/rental-manager-api${path}?listingAlias=${encodeURIComponent(listingAlias)}&bypassCache=true`,
          { credentials: 'include', headers: { accept: 'application/json' } },
        );
        if (!response.ok) {
          throw new Error(`Spotlight advertising bypass read failed with ${response.status}`);
        }
        const advertising = (await response.json())?.response as AccountViewListingAdvertisingDto | undefined;
        if (!advertising) {
          throw new Error('Spotlight advertising bypass read returned no body');
        }
        writeRowIfNewest(queryClient, queryKey, advertising, sequence);
      } catch (error) {
        // That row keeps whatever the ordinary refresh left it; the rest of the table still gets corrected.
        logError({ error, context: { listingAlias } });
      }
    }),
  );
};

// Returns the aliases a page actually wrote, so a row its page failed or left out still gets its own bypass read.
const refreshAdvertisingPagesPastCache = async (queryClient: QueryClient, pageKeys: AdvertisingPageKey[]) => {
  const refreshed = new Set<string>();
  await Promise.all(
    pageKeys.map(async (pageKey) => {
      try {
        // An ordinary read of this page still in flight would otherwise land after the bypass and put it back.
        await queryClient.cancelQueries({ queryKey: pageKey, exact: true });
        const advertisingByListingAlias = await fetchAdvertisingPage(queryClient, pageKey[1].listingAliases, {
          bypassCache: true,
        });
        queryClient.setQueryData(pageKey, advertisingByListingAlias);
        Object.keys(advertisingByListingAlias).forEach((listingAlias) => refreshed.add(listingAlias));
      } catch (error) {
        logError({ error, context: { listingAliases: pageKey[1].listingAliases } });
      }
    }),
  );
  return refreshed;
};

const getCachedAdvertising = (
  queryClient: QueryClient,
  listingAlias: string,
): AccountViewListingAdvertisingDto | undefined => {
  let result: AccountViewListingAdvertisingDto | undefined;

  queryClient.getQueriesData(buildAdvertisingQueryPredicate(listingAlias)).forEach(([, data]) => {
    if (data && typeof data === 'object' && 'listingSpotlight' in data) {
      result = data as AccountViewListingAdvertisingDto;
    }
  });

  return result;
};

/**
 * Pure function to get current placement source from cache
 */
export const getCurrentPlacementSource = (
  queryClient: QueryClient,
  listingAlias: string,
): PlacementSource | null | undefined => {
  const advertising = getCachedAdvertising(queryClient, listingAlias);
  return advertising ? advertising.listingSpotlight?.placementSource : null;
};

export const isManagedByFeedListing = (queryClient: QueryClient, listingAlias: string): boolean =>
  Boolean(getCachedAdvertising(queryClient, listingAlias)?.listingSpotlight?.managedByFeed);

/**
 * Mirrors PropertyRow's own `selectionDisabled` gate (ineligible, feed-managed read-only, or not yet loaded) so
 * programmatic selection (select-all) can't select a row the per-row checkbox itself would disable.
 */
export const isCurrentlySelectable = (queryClient: QueryClient, listingAlias: string): boolean => {
  const advertising = getCachedAdvertising(queryClient, listingAlias);
  const eligible = advertising?.listingSpotlight != null;
  return Boolean(
    eligible &&
    !isManagedByFeedReadOnly({
      buttonUrl: advertising?.buttonUrl,
      managedByFeed: advertising?.listingSpotlight?.managedByFeed,
    }),
  );
};

export type LegacySpotlightRow = {
  buttonText: string;
  buttonUrl: string;
  isSpotlighted: boolean | undefined;
};

const writeLegacySpotlightRow = (queryClient: QueryClient, listingAlias: string, row: LegacySpotlightRow): void => {
  const updated = queryClient.setQueriesData(
    buildAdvertisingQueryPredicate(listingAlias),
    (old: AccountViewListingAdvertisingDto | undefined) =>
      old
        ? {
            ...old,
            buttonText: row.buttonText,
            buttonUrl: row.buttonUrl,
            listingSpotlight: {
              ...old.listingSpotlight,
              isSpotlighted: row.isSpotlighted,
            } as AccountViewListingAdvertisingSpotlightDto,
          }
        : old,
  );
  const sequence = takeRowWriteSequence();
  updated
    .filter(([, data]) => data !== undefined)
    .forEach(([queryKey]) => claimRowWrite(queryClient, queryKey, sequence));
};

// Set the button state alongside isSpotlighted so feed-managed rows (which key off buttonUrl) flip immediately.
export const setLegacySpotlightState = (queryClient: QueryClient, listingAlias: string, isSpotlighted: boolean): void =>
  writeLegacySpotlightRow(queryClient, listingAlias, {
    buttonText: isSpotlighted ? REMOVE_SPOTLIGHT_BUTTON_TEXT : APPLY_SPOTLIGHT_BUTTON_TEXT,
    buttonUrl: isSpotlighted ? REMOVE_SPOTLIGHT_BUTTON_URL : APPLY_SPOTLIGHT_BUTTON_URL,
    isSpotlighted,
  });

// Snapshot before an optimistic write so a failure restores the exact prior state (e.g. SPOTLIGHT_REASSIGN).
export const captureLegacySpotlightSnapshot = (
  queryClient: QueryClient,
  listingAlias: string,
): LegacySpotlightRow | undefined => {
  const advertising = getCachedAdvertising(queryClient, listingAlias);
  return advertising
    ? {
        buttonText: advertising.buttonText,
        buttonUrl: advertising.buttonUrl,
        isSpotlighted: advertising.listingSpotlight?.isSpotlighted,
      }
    : undefined;
};

export const restoreLegacySpotlightSnapshot = (
  queryClient: QueryClient,
  listingAlias: string,
  snapshot: LegacySpotlightRow | undefined,
): void => {
  if (snapshot) {
    writeLegacySpotlightRow(queryClient, listingAlias, snapshot);
  }
};

/**
 * Pure function to update advertising cache optimistically
 */
export const updateAdvertisingCache = (
  queryClient: QueryClient,
  listingAlias: string,
  placementSource: PlacementSource | null,
): number => {
  const updated = queryClient.setQueriesData(
    buildAdvertisingQueryPredicate(listingAlias),
    (old: AccountViewListingAdvertisingDto | undefined) => {
      if (!old) {
        return old;
      }
      return {
        ...old,
        // Only placementSource is updated optimistically. isSpotlighted is owned by the backend (the
        // optimizer decides), so it's left untouched here and refreshed by the post-mutation advertising
        // refetch; consumers derive immediate pinned state from placementSource when needed.
        // The generated DTO marks placementSource non-nullable, but the API returns null for
        // eligible-but-not-selected rows and every consumer handles it — cast until the type is fixed.
        listingSpotlight: {
          ...old.listingSpotlight,
          placementSource,
        } as AccountViewListingAdvertisingSpotlightDto,
      };
    },
  );

  if (updated.length === 0) {
    logError({ error: new Error('No advertising queries found for listing'), context: { listingAlias } });
  }
  // Newer than any read already in flight, so a read that started before this change cannot undo it. A row with no
  // data was not written, and claiming it would drop the read that fills it.
  const sequence = takeRowWriteSequence();
  updated
    .filter(([, data]) => data !== undefined)
    .forEach(([queryKey]) => claimRowWrite(queryClient, queryKey, sequence));

  return updated.length;
};

/**
 * Pure helper to drop an item from a Set immutably
 */
export const dropFrom = <T>(set: Set<T>, item: T): Set<T> => {
  if (!set.has(item)) {
    return set;
  }
  const next = new Set(set);
  next.delete(item);
  return next;
};
